import React, { useEffect, useMemo, useRef, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, ButtonBase, InputBase } from '@material-ui/core';
import { ExpandMore, Flag, GetApp, Layers, Map as MapIcon, MyLocation, Search, Visibility, VisibilityOff } from '@material-ui/icons';
import clsx from 'clsx';
import { downloadApp } from 'utilities/download';
import { ZoneInformation, IMap, IZone, Point, ControlPointType, CromMaps } from '@pvplevelup/core/dist/interfaces/maps/IMaps';
import MapRenderer, { MapData, MapDataTypes, PointData } from 'components/Map/MapRenderer';
import { MapIcons, MapThumbnailIcons } from 'utilities/map_icons';
import { ZoneIcons } from 'utilities/zone_icons';
import { TeamColors } from 'utilities/team_colors';
import { PageHeader, PAGE_KIT } from 'components/Page/PageKit';
import CriminalImage from 'assets/images/map_icons/criminal.png';
import FunkyImage from 'assets/images/map_icons/funky.png';
import flagbase from 'assets/images/map_icons/flagbase.png';
import blueflag from 'assets/images/map_icons/blueflag.png';
import redflag from 'assets/images/map_icons/redflag.png';

/**
 * The PvP maps: every map, grouped by zone, with its objectives on toggles. The app's copy (desktop views/Maps/MapsPage.tsx)
 * also shows the player's live position; here there's no game to read it from.
 */
const ACCENT = '#42a5f5';

const MODES = [
  {key: 'teamdeathmatch', short: 'TDM', label: 'Team Deathmatch', color: '#ef5350'},
  {key: 'kingofthehill', short: 'KotH', label: 'King of the Hill', color: '#ffb300'},
  {key: 'conquest', short: 'Conquest', label: 'Conquest', color: '#66bb6a'},
  {key: 'capturetheflag', short: 'CTF', label: 'Capture the Flag', color: '#42a5f5'},
  {key: 'provinggrounds', short: 'PG', label: 'Proving Grounds', color: '#26a69a'},
] as const;

const useStyles = makeStyles((theme) => ({
  layout: {
    display: 'grid', gridTemplateColumns: '360px minmax(0, 1fr)', gridColumnGap: theme.spacing(2.5), alignItems: 'start',
    '@media (max-width: 1050px)': { gridTemplateColumns: 'minmax(0, 1fr)', gridRowGap: theme.spacing(2.5) },
  },
  panel: { background: PAGE_KIT.panel, border: PAGE_KIT.border, borderRadius: 16, boxShadow: PAGE_KIT.shadow, overflow: 'hidden' },

  // the list
  list: { position: 'sticky', top: theme.spacing(2), maxHeight: 'calc(100vh - 140px)', display: 'grid', gridTemplateRows: 'auto auto minmax(0, 1fr)' },
  listHead: { padding: theme.spacing(1.75, 1.75, 1.25), borderBottom: PAGE_KIT.divider },
  search: {
    height: 38, borderRadius: 10, background: 'rgba(0, 0, 0, 0.32)', border: '1px solid rgba(255, 255, 255, 0.12)', padding: '0 12px', fontSize: '0.88rem', color: '#fff',
    '& svg': { fontSize: 18, color: PAGE_KIT.faint, marginRight: 8 },
  },
  modeFilter: { display: 'grid', gridTemplateColumns: 'repeat(6, auto)', gridColumnGap: 6, justifyContent: 'start', marginTop: 10 },
  modeChip: {
    height: 26, padding: '0 9px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 800, color: PAGE_KIT.muted,
    background: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.08)', '&:hover': { color: '#fff' },
  },
  listBody: { overflowY: 'auto', padding: theme.spacing(0.5, 0, 1) },
  zone: { borderBottom: PAGE_KIT.divider },
  zoneHead: {
    '&&': { display: 'grid', justifyContent: 'stretch' }, gridTemplateColumns: '38px minmax(0, 1fr) auto auto', gridColumnGap: 10, alignItems: 'center',
    width: '100%', textAlign: 'left', padding: theme.spacing(1.1, 1.75), '&:hover': { background: 'rgba(255, 255, 255, 0.03)' },
  },
  zoneThumb: { width: 38, height: 38, borderRadius: 10, backgroundSize: 'cover', backgroundPosition: 'center', border: '1px solid rgba(255, 255, 255, 0.12)' },
  zoneName: { fontSize: '0.9rem', fontWeight: 800, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  zoneRegion: { fontSize: '0.72rem', color: PAGE_KIT.faint },
  zoneCount: { fontSize: '0.72rem', fontWeight: 800, color: PAGE_KIT.faint },
  chevron: { color: PAGE_KIT.faint, transition: 'transform 0.15s' },
  chevronOpen: { transform: 'rotate(180deg)' },
  mapRow: {
    '&&': { display: 'grid', justifyContent: 'stretch' }, gridTemplateColumns: '64px minmax(0, 1fr)', gridColumnGap: 10, alignItems: 'center',
    width: '100%', textAlign: 'left', padding: theme.spacing(0.85, 1.75, 0.85, 2.5), borderLeft: '3px solid transparent',
    transition: 'background 0.15s', '&:hover': { background: 'rgba(255, 255, 255, 0.04)' },
  },
  mapRowOn: { background: 'rgba(66, 165, 245, 0.1)', borderLeftColor: ACCENT, '&:hover': { background: 'rgba(66, 165, 245, 0.14)' } },
  mapThumb: { width: 64, height: 42, borderRadius: 8, backgroundSize: 'cover', backgroundPosition: 'center', border: '1px solid rgba(255, 255, 255, 0.1)', position: 'relative' },
  mapName: { fontSize: '0.86rem', fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  mapTags: { display: 'grid', gridAutoFlow: 'column', gridAutoColumns: 'max-content', gridColumnGap: 4, marginTop: 4, overflow: 'hidden' },
  empty: { padding: theme.spacing(4, 2), textAlign: 'center', color: PAGE_KIT.faint, fontSize: '0.86rem' },

  // tags
  tag: {
    display: 'inline-grid', gridAutoFlow: 'column', gridColumnGap: 4, alignItems: 'center', height: 20, padding: '0 7px', borderRadius: 6,
    fontSize: '0.64rem', fontWeight: 900, letterSpacing: '0.04em', textTransform: 'uppercase', whiteSpace: 'nowrap',
    '& img': { width: 13, height: 13 },
  },
  tagBig: { height: 26, padding: '0 10px', borderRadius: 8, fontSize: '0.72rem', '& img': { width: 16, height: 16 } },

  // the viewer
  viewer: { display: 'grid', gridRowGap: theme.spacing(2) },
  viewerHead: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gridColumnGap: theme.spacing(2), alignItems: 'center', padding: theme.spacing(2, 2.25) },
  viewerZone: { fontSize: '0.72rem', fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: PAGE_KIT.faint },
  viewerName: { fontSize: '1.5rem', fontWeight: 900, color: '#fff', letterSpacing: '-0.01em', lineHeight: 1.2, marginTop: 2 },
  viewerTags: { display: 'grid', gridAutoFlow: 'column', gridAutoColumns: 'max-content', gridColumnGap: 6, marginTop: 10 },
  canvasWrap: { position: 'relative', background: '#0b0c0f', borderTop: PAGE_KIT.divider, padding: theme.spacing(1.5), display: 'grid', justifyItems: 'center' },
  side: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gridColumnGap: theme.spacing(2), alignItems: 'start', '@media (max-width: 1300px)': { gridTemplateColumns: 'minmax(0, 1fr)', gridRowGap: theme.spacing(2) } },
  cardHead: { display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr) auto', gridColumnGap: 10, alignItems: 'center', padding: theme.spacing(1.5, 2), borderBottom: PAGE_KIT.divider },
  cardIcon: { width: 30, height: 30, borderRadius: 9, display: 'grid', placeItems: 'center', color: ACCENT, background: `${ACCENT}1f`, '& svg': { fontSize: 18 } },
  cardTitle: { fontSize: '0.92rem', fontWeight: 800, color: '#fff' },
  cardSub: { fontSize: '0.74rem', color: PAGE_KIT.faint, marginTop: 1 },
  textButton: { height: 26, padding: '0 10px', borderRadius: 7, fontSize: '0.74rem', fontWeight: 800, color: ACCENT, background: `${ACCENT}1a`, '&:hover': { background: `${ACCENT}2e` } },

  layer: {
    '&&': { display: 'grid', justifyContent: 'stretch' }, gridTemplateColumns: 'auto minmax(0, 1fr) auto', gridColumnGap: 12, alignItems: 'center',
    width: '100%', textAlign: 'left', padding: theme.spacing(1, 2), borderBottom: PAGE_KIT.divider, '&:last-child': { borderBottom: 'none' },
    '&:hover': { background: 'rgba(255, 255, 255, 0.03)' },
  },
  swatch: { width: 26, height: 26, borderRadius: 8, display: 'grid', placeItems: 'center', '& span': { width: 12, height: 12, borderRadius: 4 } },
  layerName: { fontSize: '0.86rem', fontWeight: 700, color: '#fff' },
  layerSub: { fontSize: '0.72rem', color: PAGE_KIT.faint, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  noLayers: { padding: theme.spacing(2.5, 2), fontSize: '0.84rem', color: PAGE_KIT.faint, textAlign: 'center' },

  track: { padding: theme.spacing(1.5, 2), display: 'grid', gridRowGap: 10 },
  trackHint: { fontSize: '0.74rem', color: PAGE_KIT.faint, marginTop: 1 },
}));

interface MapEntry { zone: IZone; map: IMap; }
interface Layer { key: string; label: string; sub: string; color: string; points: PointData[]; data: MapData[]; }

/** a point the renderer can draw: a circle with a radius, or a polygon of 3+ corners */
const drawable = (p: Point | null | undefined): p is Point => !!p && (
  (p.type == ControlPointType.Circle && !!p.epicenter && (p.radius || 0) > 0) || (p.type == ControlPointType.Poly && (p.coordinates?.length || 0) >= 3));
const pointsOf = (list: Array<Point | null | undefined>, style: (p: Point) => Omit<PointData, 'point'>) => list.filter(drawable).map((p) => ({point: p, ...style(p)}));
const names = (list: Array<Point | null | undefined>) => list.filter(drawable).map((p) => p.name).filter(Boolean).join(', ');

/** each kind of objective the map has, ready to draw */
function layersOf(map: IMap): Layer[] {
  const layers: Layer[] = [];
  const add = (layer: Layer) => { if(layer.points.length || layer.data.length) layers.push(layer); };
  if((map.boundary?.length || 0) >= 3)
    add({key: 'boundary', label: 'Map boundary', sub: 'Where the map ends', color: '#90a4ae', data: [],
      points: [{point: {name: 'Boundary', type: ControlPointType.Poly, coordinates: map.boundary, maxZ: 0, minZ: 0, competitive: false, initial: false}, fillColor: '#90a4ae', lineColor: '#cfd8dc', lineWidth: 2, alpha: 0.08}]});
  add({key: 'control', label: 'Control points', sub: names(map.controlPoints || []) || 'King of the Hill and Conquest', color: '#66bb6a', data: [],
    points: pointsOf(map.controlPoints || [], (p) => ({fillColor: p.competitive ? '#66bb6a' : '#eceff1', lineColor: p.initial ? '#f5e025' : '#ffffff', lineWidth: p.initial ? 3 : 1.5, alpha: 0.35}))});
  add({key: 'tdm', label: 'Team Deathmatch area', sub: 'Where TDM fights count', color: '#ab47bc', data: [],
    points: pointsOf(map.tdmboundaries || [], () => ({fillColor: '#ab47bc', lineColor: '#ce93d8', lineWidth: 2, alpha: 0.2}))});
  const flag = (p: Point | null | undefined, image: string): MapData[] => p?.epicenter ? [{type: MapDataTypes.Image, image, dWidth: 28, dHeight: 28, data: p.epicenter}] : [];
  add({key: 'ctf', label: 'Capture the Flag', sub: 'Capture zones, bases and flags', color: '#dbc714',
    points: pointsOf(map.capturetheflagPoints || [], () => ({fillColor: '#dbc714', lineColor: '#fff176', lineWidth: 2, alpha: 0.3})),
    data: [...flag(map.capturetheflagteamonebase, flagbase), ...flag(map.capturetheflagteamtwobase, flagbase), ...flag(map.capturetheflagteamoneflag, blueflag), ...flag(map.capturetheflagteamtwoflag, redflag)]});
  add({key: 'starts', label: 'Team starts', sub: 'Where each team begins', color: TeamColors[0], data: [],
    points: [...pointsOf([map.teamOneStart], () => ({fillColor: TeamColors[0], lineColor: TeamColors[0], lineWidth: 2, alpha: 0.4})),
      ...pointsOf([map.teamTwoStart], () => ({fillColor: TeamColors[1], lineColor: TeamColors[1], lineWidth: 2, alpha: 0.4}))]});
  add({key: 'dueling', label: 'Dueling area', sub: 'Where duels are fought', color: '#00aac0', data: [],
    points: pointsOf([map.duelingboundary], () => ({fillColor: '#00aac0', lineColor: '#4dd0e1', lineWidth: 2, alpha: 0.22}))});
  add({key: 'pg', label: 'Proving Grounds area', sub: 'Where proving grounds matches count', color: '#26a69a', data: [],
    points: pointsOf([map.pvpprovinggroundsboundary], () => ({fillColor: '#0c9e44', lineColor: '#69f0ae', lineWidth: 2, alpha: 0.2}))});
  return layers;
}

/** every PvP map (playable in a mode, or the proving grounds), by zone */
function allZones(): Array<{zone: IZone, maps: IMap[]}> {
  return Object.values(ZoneInformation as Record<string, IZone>)
    .filter((zone) => zone.enabled)
    .map((zone) => ({zone, maps: Object.values(zone.mapInfo || {}).filter((m) => m && (m.teamdeathmatch || m.kingofthehill || m.conquest || m.capturetheflag || m.provinggrounds))
      .sort((a, b) => a.name.localeCompare(b.name))}))
    .filter((z) => z.maps.length > 0)
    .sort((a, b) => a.zone.region.localeCompare(b.zone.region) || a.zone.name.localeCompare(b.zone.name));
}

const Tag: React.FC<{color: string, big?: boolean, icon?: string, title?: string}> = ({color, big, icon, title, children}) => {
  const classes = useStyles();
  return <span className={clsx(classes.tag, {[classes.tagBig]: big})} title={title} style={{color, background: `${color}1f`, boxShadow: `inset 0 0 0 1px ${color}40`}}>{icon && <img src={icon} alt="" />}{children}</span>;
};

/** the map's flags as tags: event only, criminal, funky, and (big) the modes it's played in */
export const MapTags: React.FC<{map: IMap, big?: boolean, modes?: boolean}> = ({map, big, modes}) => (
  <React.Fragment>
    {map.eventOnly && <Tag big={big} color="#ce93d8" title="Only on the day's event zone">Event only</Tag>}
    {map.criminal && <Tag big={big} color="#ef5350" icon={CriminalImage} title="A criminal map">Criminal</Tag>}
    {map.funky && <Tag big={big} color="#ffca28" icon={FunkyImage} title="A funky map">Funky</Tag>}
    {modes && MODES.filter((m) => (map as any)[m.key]).map((m) => <Tag key={m.key} big={big} color={m.color} title={m.label}>{m.short}</Tag>)}
    {modes && CromMaps.includes(map.id) && <Tag big={big} color="#90caf9" title="Also played on Crom">Crom</Tag>}
  </React.Fragment>
);

/** the canvas's width follows its box */
function useWidth(){
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if(!ref.current) return;
    const measure = () => setWidth(Math.floor(ref.current?.clientWidth || 0));
    measure();
    const observer = new (window as any).ResizeObserver(measure);
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

export const MapsPage: React.FC = () => {
  const classes = useStyles();
  const zones = useMemo(allZones, []);
  const [search, setSearch] = useState('');
  const [mode, setMode] = useState<string>('');
  const [open, setOpen] = useState<Record<number, boolean>>({});
  const [selected, setSelected] = useState<MapEntry | null>(() => zones[0] ? {zone: zones[0].zone, map: zones[0].maps[0]} : null);
  const [hidden, setHidden] = useState<Record<string, boolean>>({});
  const [canvasRef, width] = useWidth();

  // the first zone starts open
  useEffect(() => { if(zones[0]) setOpen((o) => ({[zones[0].zone.id]: true, ...o})); }, []);

  const q = search.trim().toLowerCase();
  const shown = zones.map((z) => ({...z, maps: z.maps.filter((m) => (!mode || (m as any)[mode]) && (!q || m.name.toLowerCase().includes(q) || z.zone.name.toLowerCase().includes(q)))}))
    .filter((z) => z.maps.length > 0);
  const layers = useMemo(() => selected ? layersOf(selected.map) : [], [selected?.map.id]);
  const visible = layers.filter((l) => hidden[l.key] === false || (hidden[l.key] == null && l.key == 'control'));
  const isOn = (l: Layer) => visible.includes(l);
  const toggle = (l: Layer) => setHidden((h) => ({...h, [l.key]: isOn(l)}));

  const data: MapData[] = visible.flatMap((l) => l.data);
  const pointData: PointData[] = visible.flatMap((l) => l.points);

  return (
    <div className="content-container">
      <PageHeader icon={<MapIcon />} title="PvP Maps" subtitle="Every PvP map by zone. Turn on objectives to see where they are." />
      <div className={classes.layout}>
        <div className={clsx(classes.panel, classes.list)}>
          <div className={classes.listHead}>
            <InputBase className={classes.search} fullWidth startAdornment={<Search />} placeholder="Search maps or zones" value={search} onChange={(e) => setSearch(e.target.value)} />
            <div className={classes.modeFilter}>
              {[{key: '', short: 'All', color: ACCENT}, ...MODES].map((m) => (
                <ButtonBase key={m.key} className={classes.modeChip} onClick={() => setMode(m.key)}
                  style={mode == m.key ? {color: '#fff', background: `${m.color}2e`, borderColor: `${m.color}aa`} : undefined}>{m.short}</ButtonBase>
              ))}
            </div>
          </div>
          <span />
          <div className={classes.listBody}>
            {shown.length == 0 && <div className={classes.empty}>No maps match.</div>}
            {shown.map(({zone, maps}) => {
              const expanded = !!q || !!mode || !!open[zone.id];
              return (
                <div key={zone.id} className={classes.zone}>
                  <ButtonBase component="div" role="button" className={classes.zoneHead} onClick={() => setOpen((o) => ({...o, [zone.id]: !expanded}))}>
                    <span className={classes.zoneThumb} style={{backgroundImage: `url(${ZoneIcons[zone.id]})`}} />
                    <span style={{minWidth: 0}}>
                      <div className={classes.zoneName}>{zone.name}</div>
                      <div className={classes.zoneRegion}>{zone.region}</div>
                    </span>
                    <span className={classes.zoneCount}>{maps.length}</span>
                    <ExpandMore className={clsx(classes.chevron, {[classes.chevronOpen]: expanded})} />
                  </ButtonBase>
                  {expanded && maps.map((map) => (
                    <ButtonBase key={map.id} component="div" role="button" className={clsx(classes.mapRow, {[classes.mapRowOn]: selected?.map.id == map.id})}
                      onClick={() => setSelected({zone, map})}>
                      <span className={classes.mapThumb} style={{backgroundImage: `url(${MapThumbnailIcons[zone.id]?.[map.id]})`}} />
                      <span style={{minWidth: 0}}>
                        <div className={classes.mapName}>{map.name}</div>
                        <div className={classes.mapTags}><MapTags map={map} /></div>
                      </span>
                    </ButtonBase>
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        {selected &&
          <div className={classes.viewer}>
            <div className={classes.panel}>
              <div className={classes.viewerHead}>
                <div>
                  <div className={classes.viewerZone}>{selected.zone.name} · {selected.zone.region}</div>
                  <div className={classes.viewerName}>{selected.map.name}</div>
                  <div className={classes.viewerTags}><MapTags map={selected.map} big modes /></div>
                </div>
              </div>
              <div className={classes.canvasWrap} ref={canvasRef}>
                {width > 0 &&
                  <MapRenderer key={selected.map.id} isResizing={false} width={Math.min(width - 24, 1100)}
                    src={MapIcons[selected.zone.id]?.[selected.map.id] || ''} data={data} pointData={pointData}
                    xStart={selected.map.xStart} yStart={selected.map.yStart} xDistance={selected.map.xDistance} yDistance={selected.map.yDistance}
                    zoneSrc={ZoneIcons[selected.zone.id]} zoneXStart={0} zoneYStart={0} zoneXDistance={selected.zone.width} zoneYDistance={selected.zone.height} />}
              </div>
            </div>

            <div className={classes.side}>
              <div className={classes.panel}>
                <div className={classes.cardHead}>
                  <span className={classes.cardIcon}><Layers /></span>
                  <span>
                    <div className={classes.cardTitle}>Objectives</div>
                    <div className={classes.cardSub}>Show them on the map</div>
                  </span>
                  {layers.length > 0 &&
                    <ButtonBase className={classes.textButton} onClick={() => setHidden(Object.fromEntries(layers.map((l) => [l.key, visible.length == layers.length])))}>
                      {visible.length == layers.length ? 'Hide all' : 'Show all'}
                    </ButtonBase>}
                </div>
                {layers.length == 0 && <div className={classes.noLayers}>This map has no objectives marked.</div>}
                {layers.map((l) => {
                  const on = isOn(l);
                  return (
                    <ButtonBase key={l.key} component="div" role="switch" aria-checked={on} className={classes.layer} onClick={() => toggle(l)}>
                      <span className={classes.swatch} style={{background: `${l.color}${on ? '2e' : '12'}`}}>{l.key == 'ctf' ? <Flag style={{fontSize: 15, color: l.color}} /> : <span style={{background: l.color, opacity: on ? 1 : 0.45}} />}</span>
                      <span style={{minWidth: 0}}>
                        <div className={classes.layerName} style={{opacity: on ? 1 : 0.7}}>{l.label}</div>
                        <div className={classes.layerSub}>{l.sub}</div>
                      </span>
                      {on ? <Visibility style={{fontSize: 18, color: l.color}} /> : <VisibilityOff style={{fontSize: 18, color: PAGE_KIT.faint}} />}
                    </ButtonBase>
                  );
                })}
              </div>

              <div className={classes.panel}>
                <div className={classes.cardHead}>
                  <span className={classes.cardIcon} style={{color: '#f48fb1', background: 'rgba(244, 143, 177, 0.12)'}}><MyLocation /></span>
                  <span>
                    <div className={classes.cardTitle}>See yourself on the map</div>
                    <div className={classes.cardSub}>In the PvP LevelUp app</div>
                  </span>
                  <span />
                </div>
                <div className={classes.track}>
                  <div className={classes.trackHint} style={{fontSize: '0.84rem', lineHeight: 1.5, color: PAGE_KIT.muted}}>
                    The app's Maps page shows where you are as you move in game, as in a match, and can follow you from map to map.
                  </div>
                  <Button variant="outlined" startIcon={<GetApp />} onClick={downloadApp} style={{justifySelf: 'start', textTransform: 'none', fontWeight: 800, borderRadius: 10}}>Get the app</Button>
                </div>
              </div>
            </div>
          </div>}
      </div>
    </div>
  );
};

export default MapsPage;
